import {
  ArrowLeft,
  ArrowRight,
  LinkIcon,
  LockIcon,
  SettingsIcon,
  ShareIcon,
} from "lucide-react";
import { CSSProperties, ReactNode } from "react";
import { cn } from "../../lib/utils";

export interface SafariProps {
  url?: string;
  width?: number;
  height?: number;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function Safari({
  url = "example.com",
  width = 1203,
  height = 753,
  children,
  className,
  style,
}: SafariProps) {
  const toolbarHeight = 52;
  const contentHeight = height - toolbarHeight;

  return (
    <div
      className={cn(
        "@container overflow-hidden rounded-xl drop-shadow-2xl border-2 border-muted",
        className,
      )}
      style={{
        width,
        height,
        ...style,
      }}
    >
      {/* Safari Toolbar */}
      <div
        className="flex items-center bg-muted dark:bg-[#404040] px-4"
        style={{ height: toolbarHeight }}
      >
        {/* Traffic lights */}
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 dark:bg-[#525252]" />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 dark:bg-[#525252]" />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 dark:bg-[#525252]" />
        </div>

        {/* Back/Forward buttons */}
        <div className="@md:flex hidden items-center gap-3 ml-6">
          <ArrowLeft className="size-4 text-neutral-400 hover:text-neutral-600 cursor-pointer" />
          <ArrowRight className="size-4 text-neutral-400 hover:text-neutral-600 cursor-pointer" />
        </div>

        {/* URL bar */}
        <div className="flex-1 mx-3 @md:mx-6">
          <div className="bg-muted-foreground/10 dark:bg-[#262626] rounded-md px-3 py-1.5 flex items-center gap-2">
            <LockIcon className="size-3 text-neutral-500" />
            <span className="text-xs text-muted-foreground/80 truncate">
              {url}
            </span>
          </div>
        </div>

        <div className="@md:flex hidden items-center gap-4">
          {/* Share button */}
          <ShareIcon className="size-4 text-muted-foreground/40 hover:text-neutral-600 cursor-pointer" />
          <LinkIcon className="size-4 text-muted-foreground/40 hover:text-neutral-600 cursor-pointer" />
          <SettingsIcon className="size-4 text-muted-foreground/40 hover:text-neutral-600 cursor-pointer" />
        </div>
      </div>

      {/* Content area */}
      <div
        className="relative bg-[#f8f9fa] dark:bg-[#1a1a1a] overflow-hidden"
        style={{ height: contentHeight }}
      >
        {children && (
          <div className="w-full h-full overflow-auto">{children}</div>
        )}
      </div>
    </div>
  );
}
